import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface SelectedRecords {
  education: number[];
  employment: number[];
  licenses: number[];
}

/** Proof-of-submission, kept after the case is created so the confirm step
 * survives reloads and shows the real case IDs. */
export interface VerificationReceipt {
  caseId: number | null;
  verificationIds: number[];
  pendingIds: number[];
  signatureDataUrl: string | null;
  createdAt: string;
}

interface VerificationFlowState {
  pricingAccepted: boolean;
  otpVerified: boolean;
  selected: SelectedRecords;
  signatureDataUrl: string | null;
  caseId: number | null;
  /** Case IDs the backend created for this submission. */
  verificationIds: number[];
  /** Case IDs already pending from a prior submission (409 conflicts). */
  pendingIds: number[];
  receipt: VerificationReceipt | null;

  setPricingAccepted: (v: boolean) => void;
  setOtpVerified: (v: boolean) => void;
  toggleRecord: (kind: keyof SelectedRecords, id: number) => void;
  setSignature: (dataUrl: string | null) => void;
  setCaseId: (id: number | null) => void;
  setReceipt: (receipt: VerificationReceipt | null) => void;
  clearReceipt: () => void;
  reset: () => void;
}

const initial = {
  pricingAccepted: false,
  otpVerified: false,
  selected: { education: [], employment: [], licenses: [] },
  signatureDataUrl: null as string | null,
  caseId: null as number | null,
  verificationIds: [] as number[],
  pendingIds: [] as number[],
  receipt: null as VerificationReceipt | null,
};

export const useCandidateVerificationStore = create<VerificationFlowState>()(
  persist(
    (set) => ({
      ...initial,
      setPricingAccepted: (v) => set({ pricingAccepted: v }),
      setOtpVerified: (v) => set({ otpVerified: v }),
      toggleRecord: (kind, id) =>
        set((s) => {
          const list = s.selected[kind];
          const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
          return { selected: { ...s.selected, [kind]: next } };
        }),
      setSignature: (dataUrl) => set({ signatureDataUrl: dataUrl }),
      setCaseId: (id) => set({ caseId: id }),
      setReceipt: (receipt) => set({ receipt }),
      clearReceipt: () => set({ receipt: null }),
      reset: () =>
        set({
          ...initial,
          selected: { education: [], employment: [], licenses: [] },
          verificationIds: [],
          pendingIds: [],
        }),
    }),
    {
      name: 'tally:candidate-verification',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
